/**
 * +field: The Hollow Barrow's mobs, mini-boss and end boss. Normal difficulty runs at mobLevel 4
 * (levelMult scaling, keystone ×1), so numbers are in L10 units like every other NPC. Pacing
 * target: 5–8 min for a level-4 hero + 3 Group Finder bots (≈ 9k party DPS in L10 units with the
 * level-4 kits), mechanics gentle and readable — this is the game's first dungeon.
 *
 *   Barrow Ghoul (fodder 32k) · Barrow Wisp (caster 59k, kickable Grave Bolt) · Barrow Crawler
 *   (standard 91k, Carrion Gnaw) · Bonecaller (miniboss 322k: Bone Spikes rings) · the Hollow King
 *   (boss 580k: Grave Cleave frontal + Call of the Barrow ghoul adds).
 */
import type { EncounterDef, EnemyAbility, NpcDef } from '@keystone/sim';
import { npcMelee } from '../balance';
import { enemy, hit, mech, telegraph } from '../npcs/common';
import { BARROW_ARENA } from './barrowLayout';
import { BARROW_ENCOUNTER, MOB, MOB_ABILITY } from './ids';

// ---------------------------------------------------------------------------
// Trash abilities
// ---------------------------------------------------------------------------

const graveBolt = enemy({
  id: MOB_ABILITY.graveBolt,
  name: 'Grave Bolt',
  school: 'shadow',
  kind: 'bolt',
  description: 'Interruptible shadow bolt at a random player.',
  cast: { type: 'cast', timeMs: 2000 },
  interruptible: true,
  timer: { first: 2000, every: 7000, jitter: 1000 },
  target: 'random',
  priority: 1,
  kickPriority: 1,
  counter: ['interrupt'],
  effects: [hit('shadow', 4500)],
});

const carrionGnaw = enemy({
  id: MOB_ABILITY.carrionGnaw,
  name: 'Carrion Gnaw',
  school: 'physical',
  kind: 'tankbuster',
  description: 'A heavy bite on the tank.',
  cast: { type: 'cast', timeMs: 1500 },
  range: 8,
  timer: { first: 6000, every: 14000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [hit('physical', 6000)],
});

// ---------------------------------------------------------------------------
// The Bonecaller (mini-boss): one mechanic — Bone Spikes rings under two players
// ---------------------------------------------------------------------------

const SPIKE_RING = { type: 'circle' as const, radius: 4 };
const boneSpikes = enemy({
  id: MOB_ABILITY.boneSpikes,
  name: 'Bone Spikes',
  school: 'physical',
  kind: 'groundAoE',
  description: 'Orange rings under two random players erupt into bone spikes after 2.5 s. Step out.',
  cast: { type: 'cast', timeMs: 1500 },
  timer: { first: 7000, every: 15000, jitter: 1500 },
  target: 'none',
  shape: SPIKE_RING,
  priority: 3,
  counter: ['dodge', 'spread'],
  warning: { text: 'Bone Spikes', severity: 'info' },
  effects: [mech({ kind: 'pick', rule: 'random', count: 2, effects: [telegraph(SPIKE_RING, 'target', 2500, [hit('physical', 9000, { avoidable: false })])] })],
});

/** Filler between spikes (the healer's work). Not interruptible: the Wisps are the kick lesson. */
const boneShard = enemy({
  id: MOB_ABILITY.boneShard,
  name: 'Bone Shard',
  school: 'physical',
  kind: 'bolt',
  description: 'Flings a bone shard at a random player.',
  cast: { type: 'cast', timeMs: 1500 },
  timer: { first: 3000, every: 6000, jitter: 1000 },
  target: 'random',
  counter: ['healerCd'],
  effects: [hit('physical', 3500)],
});

// ---------------------------------------------------------------------------
// The Hollow King: Grave Cleave (telegraphed frontal on the tank) + Call of the Barrow (ghoul adds)
// ---------------------------------------------------------------------------

const CLEAVE_CONE = { type: 'cone' as const, range: 14, angle: (5 * Math.PI) / 9 };
const graveCleave = enemy({
  id: MOB_ABILITY.graveCleave,
  name: 'Grave Cleave',
  school: 'shadow',
  kind: 'frontal',
  description: 'Raises his blade, then cleaves a wide cone in front of him. Everyone (tank included) steps out of the orange.',
  cast: { type: 'cast', timeMs: 1500 },
  range: 10,
  timer: { first: 9000, every: 16000, jitter: 1000 },
  target: 'victim',
  shape: CLEAVE_CONE,
  lockFacing: true,
  priority: 2,
  counter: ['dodge'],
  warning: { text: 'Grave Cleave', severity: 'danger' },
  effects: [telegraph(CLEAVE_CONE, 'caster', 2000, [hit('shadow', 16000, { avoidable: false })])],
});

const callOfTheBarrow = enemy({
  id: MOB_ABILITY.callOfTheBarrow,
  name: 'Call of the Barrow',
  school: 'shadow',
  kind: 'summon',
  description: 'Two Barrow Ghouls claw out of the floor. Tank picks them up; DPS cleave them down.',
  cast: { type: 'cast', timeMs: 2000 },
  timer: { first: 15000, every: 30000 },
  target: 'none',
  priority: 4,
  counter: ['killAdd'],
  warning: { text: 'Call of the Barrow', severity: 'info' },
  effects: [{ type: 'summon', npcId: MOB.barrowGhoul, count: 2, radius: 8, anchor: 'caster' }],
});

export const BARROW_ABILITIES: EnemyAbility[] = [graveBolt, carrionGnaw, boneSpikes, boneShard, graveCleave, callOfTheBarrow];

// ---------------------------------------------------------------------------
// NPCs
// ---------------------------------------------------------------------------

export const BARROW_NPCS: NpcDef[] = [
  {
    id: MOB.barrowGhoul,
    name: 'Barrow Ghoul',
    tier: 'fodder',
    hpMult: 0.35,
    forces: 2,
    melee: npcMelee(700, 2000),
    abilities: [],
    radius: 0.8,
    combatReach: 1.3,
    moveSpeed: 6.5,
    color: '#8a9a7a',
  },
  {
    id: MOB.barrowWisp,
    name: 'Barrow Wisp',
    tier: 'caster',
    hpMult: 0.4,
    forces: 4,
    melee: npcMelee(300, 2000),
    abilities: [MOB_ABILITY.graveBolt],
    radius: 0.8,
    combatReach: 1.3,
    moveSpeed: 7,
    preferredRange: 25,
    color: '#7fd8ff',
  },
  {
    id: MOB.barrowCrawler,
    name: 'Barrow Crawler',
    tier: 'standard',
    hpMult: 0.55,
    forces: 5,
    melee: npcMelee(1300, 2200),
    abilities: [MOB_ABILITY.carrionGnaw],
    radius: 1.3,
    combatReach: 2,
    moveSpeed: 6.5,
    color: '#5c4a6e',
  },
  {
    id: MOB.barrowBonecaller,
    name: 'The Bonecaller',
    tier: 'miniboss',
    hpMult: 0.7,
    forces: 0,
    melee: npcMelee(1600, 2200),
    abilities: [MOB_ABILITY.boneSpikes, MOB_ABILITY.boneShard],
    radius: 1.6,
    combatReach: 2.5,
    moveSpeed: 7,
    ccImmune: true,
    tauntDr: true,
    color: '#d8d0b0',
  },
  {
    id: MOB.barrowKing,
    name: 'The Hollow King',
    tier: 'boss',
    hpMult: 0.45,
    forces: 0,
    melee: npcMelee(2400, 2400),
    abilities: [MOB_ABILITY.graveCleave, MOB_ABILITY.callOfTheBarrow],
    radius: 2.4,
    combatReach: 3.5,
    moveSpeed: 7,
    ccImmune: true,
    tauntDr: true,
    color: '#4b6a8a',
  },
];

// ---------------------------------------------------------------------------
// Encounters
// ---------------------------------------------------------------------------

export const BARROW_ENCOUNTERS: EncounterDef[] = [
  {
    id: BARROW_ENCOUNTER.bonecaller,
    name: 'The Bonecaller',
    bossNpcIds: [MOB.barrowBonecaller],
    phases: [{ abilities: [MOB_ABILITY.boneSpikes, MOB_ABILITY.boneShard] }],
    enrageMs: 240_000,
    arena: BARROW_ARENA.bonecaller,
  },
  {
    id: BARROW_ENCOUNTER.king,
    name: 'The Hollow King',
    bossNpcIds: [MOB.barrowKing],
    phases: [{ abilities: [MOB_ABILITY.graveCleave, MOB_ABILITY.callOfTheBarrow] }],
    enrageMs: 300_000,
    arena: BARROW_ARENA.king,
  },
];
